import React from 'react';
import FormModal from '@shared/ui/FormModal';

export interface SaveFilterPreviewItem {
  label: string;
  value: string;
}

interface SaveFilterModalProps {
  isOpen: boolean;
  filterName: string;
  onFilterNameChange: (name: string) => void;
  onCancel: () => void;
  onSave: () => void;
  saving?: boolean;
  /** Lines shown under "Current filters:"; empty renders "No filters applied". */
  previewItems?: SaveFilterPreviewItem[];
}

/**
 * Shared "Save Filter" modal — the canonical pattern used by the Legislation,
 * Campaign Finance, and Voter Data pages. Keep this the single source of truth so
 * the three stay identical.
 */
const SaveFilterModal: React.FC<SaveFilterModalProps> = ({
  isOpen,
  filterName,
  onFilterNameChange,
  onCancel,
  onSave,
  saving = false,
  previewItems = [],
}) => {
  return (
    <FormModal
      isOpen={isOpen}
      title="Save filter"
      onClose={onCancel}
      onSubmit={onSave}
      size="sm"
      submitLabel={saving ? 'Saving…' : 'Save filter'}
      isSubmitting={saving}
      submitDisabled={!filterName.trim()}
      isDirty={!!filterName.trim()}
    >
      <div>
        <label
          htmlFor="saveFilterName"
          className="mb-2 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary"
        >
          Filter name
        </label>
        {/* Enter and Escape are handled by <FormModal>: the input sits in a real
            <form>, so Enter submits, and the dialog owns Escape. */}
        <input
          id="saveFilterName"
          type="text"
          value={filterName}
          onChange={(e) => onFilterNameChange(e.target.value)}
          placeholder="Enter a name for this filter…"
          className="input"
          autoFocus
        />
      </div>

      <div className="mt-4">
        <p className="mb-2 text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
          Current filters
        </p>
        <div className="surface-inset p-3">
          <div className="space-y-1 text-xs text-text-secondary dark:text-text-dark-secondary">
            {previewItems.length === 0 ? (
              <div className="italic text-text-tertiary dark:text-text-dark-tertiary">
                No filters applied
              </div>
            ) : (
              previewItems.map((item) => (
                <div key={item.label}>
                  {item.label}: {item.value}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </FormModal>
  );
};

export default SaveFilterModal;
